export enum OrderType {
  PreOrder = 'Pre-Order',
  ReOrder = 'Re-Order',
  MultiplePreOrder = 'Pre-Order-Multiple',
  DropShip = 'Drop ship',
  ReturnOrder = 'Return',
  WarrantyOrder = 'Warranty',
  Bulks = 'Bulks',
  Replacement = 'Replacement',
  DemoSets = 'Demo Sets',
  ConsignmentReturn = 'Consignment Return',
  ConsignmentSale = 'Consignment Sale',
  EventOrder = 'Event Order',
  ExperienceItems = 'Experience Items',
  LastSeason = 'Last Season',
  MarketingMaterial = 'Marketing Material',
  MediaSamples = 'Media Samples',
  SalesTeamSamples = 'Sales Team Samples',
  Sponsorship = 'Sponsorship',
  Subscription = 'Subscription',
  /**
   * A tailored, seasonalized collection (e.g. `SP FW27`, `SP SS27`) with its own
   * dedicated sell-in window, accessible only to eligible accounts.
   *
   * - Cannot be mixed with regular pre-order items in the same order.
   * - Cannot be re-ordered.
   * - Gated by the `SPECIAL_PROJECTS_ENABLED` feature flag.
   *
   * Maps to {@link OrderTypeEnum.PreOrder} in Dynamics (no native Special Projects type exists).
   */
  SpecialProjects = 'Special Projects',
  Unknown = '', // used as default value in the mapper to avoid undefined
}

/**
 * The seasonalized order types — collections tied to a specific season with
 * their own sell-in window (`Pre-Order`, `Pre-Order-Multiple`, `Special Projects`).
 */
const SEASONAL_ORDER_TYPES: ReadonlySet<OrderType> = new Set([
  OrderType.PreOrder,
  OrderType.MultiplePreOrder,
  OrderType.SpecialProjects,
]);

/**
 * Returns `true` when the order type is a pre-order variant
 * (`Pre-Order` or `Pre-Order-Multiple`).
 */
export const isPreOrderVariant = (orderType: OrderType | null | undefined): boolean =>
  orderType === OrderType.PreOrder || orderType === OrderType.MultiplePreOrder;

/**
 * Returns `true` when the order type is a re-order variant (`Re-Order` or `Drop ship`).
 * Re-orders and drop ships share season/eligibility handling across the app.
 */
export const isReOrderVariant = (orderType: OrderType | null | undefined): boolean =>
  orderType === OrderType.ReOrder || orderType === OrderType.DropShip;

/**
 * Returns `true` when the order type is seasonalized: a pre-order variant or
 * Special Projects. These share season/sell-in handling across the app.
 *
 * Single source of truth for both the API and the web frontend.
 */
export const isSeasonalOrderType = (orderType: OrderType | null | undefined): boolean =>
  orderType != null && SEASONAL_ORDER_TYPES.has(orderType);
